<script setup>
import { ref, computed, watch, nextTick, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { $bus } from '@/common/events.js'
import { guid } from '@/common/helper.js'

const uniqueId = ref(guid());

const props = defineProps({
  id: {
    type: String,
    default: ""
  },
  name: {
    type: String,
    default: ""
  },
  enabled: {
    type: Boolean,
    default: true
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  severity: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: ""
  },
  validation: {
    type: Object,
    default: null
  },
  maxVisiblePageButtons: {
    type: Number,
    default: 7
  },
  options: {
    type: Array,
    default: [ /* { name } */ ]
  },
  //selected: {
  //  type: String,
  //  default: null
  //},
  modelValue: {
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    emit('change', value);
  }
});

//Optional icon of the selected item
const icon = computed(() => {
  let result = null;
  let id = value ? value.value : null;
  if (id) {
    let option = props.options.find(x => x.id == id || x.value == id);
    result = option && option.icon ? option.icon : null;
  }
  return result;
});

//Name or text of the selected item
const text = computed(() => {
  let result = null;
  let id = value ? value.value : null;
  if (id) {
    let option = props.options.find(x => x.id == id || x.value == id);
    result = option ? (option.text || option.name) : null;
  }
  return result;
});
const isDropDownMenuVisible = ref(false);

watch(isDropDownMenuVisible, (visible) => {
  if (visible) {
    document.addEventListener("click", hideDropDownMenu);
  }
  else {
    document.removeEventListener("click", hideDropDownMenu);
  }
});

onUnmounted(() => {
  document.removeEventListener("click", hideDropDownMenu);
});
const isPreventHide = ref(false);

function showDropDownMenu() {
  isDropDownMenuVisible.value = true;
  hideKeyboard();
  //document.getElementById(uniqueId.value).focus();
  highlighted.value = selected.value;
}

function hideDropDownMenu() {
  if (isPreventHide.value) {
    return;
  }
  isDropDownMenuVisible.value = false;
}

function toggleDropDownMenu(force) {
  if (!props.enabled) {
    return;
  }
  //isDropDownMenuVisible.value ^= true;
  if (!isDropDownMenuVisible.value) {
    showDropDownMenu();
  }
  else {
    if (force) {
      isDropDownMenuVisible.value = false;
    }
    else hideDropDownMenu();
  }
}

const selected = computed(() => value.value);
const highlighted = ref();
function selectOption(option) {
  //Note: Firefox with CTRL+M does not trigger this event!
  value.value = option.id || option.value;
  isPreventHide.value = false;
  hideDropDownMenu();
}

function getOption(id) {
  return id ? props.options.find(x => x.id == id || x.value == id) : null;
}

function getHighlightedIndex() {
  let id = highlighted.value;
  let index = id ? props.options.findIndex(x => x.id == id || x.value == id) : null;
  return index;
}

function selectSibling(e) {
  let index = null;
  let option = null;

  switch (e.keyCode) {
    case 38: //Up
      e.preventDefault();

      index = getHighlightedIndex();

      if (index >= 0) {
        index--;
      }
      else {
        return;
      }

      if (index < 0) {
        return;
      }

      //Prev
      highlighted.value = props.options[index]?.id || props.options[index]?.value;
      break;

    case 40: //Down
      e.preventDefault();

      index = getHighlightedIndex();

      if (index >= 0) {
        index++;
      }
      else {
        return;
      }

      if (index >= props.options.length) {
        return;
      }

      //Next
      highlighted.value = props.options[index]?.id || props.options[index]?.value;
      break;

    case 13: //Enter
      e.preventDefault();

      option = getOption(highlighted.value);
      if (option) {
        selectOption(option);
      }
      break;

    case 27: //Escape
      e.preventDefault();
      hideDropDownMenu();
      break;
  }
}

function hideKeyboard() {
  if (props.enabled && !props.readonly) {
    $bus.emit("keyboard", { visible: false });
  }
}
</script>

<template>
  <div class="relative dropdown" :style="$attrs.style">
    <!-- Dropdown button - new design -->
    <button type="button" class="relative w-full dropdown-button" :disabled="!props.enabled"
      :class="{ 'dropdown-danger': props.severity == 'danger', 'dropdown-warning': props.severity == 'warning' }" :style="$attrs.style"
      @click="toggleDropDownMenu(true)" @keydown="(e) => selectSibling(e)" @mouseover="isPreventHide = true" @mouseleave="isPreventHide = false">
      <div class="dropdown-button-inner">
        <i class="w-4 mr-2 text-center fa" :class="icon" v-if="icon && text"></i>
        <span class="dropdown-button-text" v-if="text">{{ text }}</span>
        <span class="dropdown-button-placeholder" v-else>{{ props.placeholder }}</span>
        <svg class="dropdown-button-arrow" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 4 4 4-4"/>
        </svg>
      </div>
    </button>

    <!-- Dropdown button - not in use -->
    <button type="button" class="relative w-full dropdown-button" :disabled="!props.enabled" v-if="false"
      :class="{ 'dropdown-danger': props.severity == 'danger', 'dropdown-warning': props.severity == 'warning' }" :style="$attrs.style"
      @click="toggleDropDownMenu(true)" @keydown="(e) => selectSibling(e)" @mouseover="isPreventHide = true" @mouseleave="isPreventHide = false">
      <div class="dropdown-button-inner flex flex-row justify-between gap-2 w-full">
        <div class="flex flex-row gap-2 w-full text-left">
          <div class="grid place-items-center mr-1" v-if="icon && text"><i :class="icon"></i></div>
          <div v-if="text" class="text-ellipsis truncate w-full">{{ text }}</div>
          <div v-else>{{ props.placeholder }}</div>
        </div>
        <div class="grid place-items-center">
          <svg class="w-2.5 h-2.5 mr-1.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 4 4 4-4"/>
          </svg>
        </div>
      </div>
    </button>

    <!-- Dropdown menu -->
    <div class="absolute z-10 w-full dropdown-menu" :style="$attrs.style"
      v-if="isDropDownMenuVisible && props.options && props.options.length">
      <ul class="py-2 text-sm dark:text-gray-200" aria-labelledby="dropdownDelayButton">
        <li v-for="option in props.options">
          <a href="#" class="dropdown-menu-item" :class="{ 'bg-blue-100': option.id == highlighted }"
            @click.prevent="selectOption(option)" @mouseover="isPreventHide = true" @mouseleave="isPreventHide = false">
            <i class="w-4 mr-2 text-center fa" :class="option.icon" v-if="option.icon"></i>
            <span class="text-sm text-gray-700 dark:text-gray-200" :class="{ 'font-medium': !!option.selected }">{{ option.name }}</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Invisible input just to handle focus and unfocus events -->
    <input type="text" :id="uniqueId" value="" @blur="hideDropDownMenu" class="fixed -left-96 -top-96 w-0 h-0 bg-transparent text-transparent border-none pointer-events-none" />
  </div>

  <div class="dropdown-validation-message" v-if="!!props.validation">
    {{ typeof validation == "string" ? validation : validation.message }}
  </div>
</template>

<style scoped>
.dropdown-button {
  max-height: 42px;
  @apply block bg-white border border-gray-300
    text-sm rounded-md whitespace-nowrap
    text-gray-400
    hover:bg-gray-50
    focus:bg-gray-100 focus:text-gray-700
    focus:ring-blue-500 focus:border-blue-500 focus:border-2
    hover:ring-gray-400 hover:border-gray-400
    dark:focus:ring-blue-800 dark:bg-gray-700 dark:border-gray-600
    dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white;
    box-sizing: border-box;
    outline: none;
}

.dropdown-button:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  --tw-ring-inset: var(--tw-empty, );
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: #1C64F2;
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
  border-color: #1C64F2;
}

.dropdown-button:disabled {
  @apply bg-white hover:bg-white focus:bg-white
    text-gray-400 hover:text-gray-400 focus:text-gray-400
    border-gray-300 hover:border-gray-300 focus:border-gray-300
    dark:bg-gray-800 dark:border-gray-700;
}

.dropdown-button .dropdown-button-text {
  @apply whitespace-nowrap text-gray-900 dark:text-gray-300;
}

.dropdown-button:hover .dropdown-button-text {
  @apply text-gray-700 dark:text-gray-200;
}

.dropdown-button:disabled .dropdown-button-text,
.dropdown-button:disabled:hover .dropdown-button-text {
  @apply text-gray-400 dark:text-gray-300;
}

.dropdown-button .dropdown-button-inner {
  @apply relative inline-flex w-full m-2.5 items-center select-none pr-5;
  /* @apply relative w-full p-2.5 items-center select-none pr-5; */
}

.dropdown-button .dropdown-button-arrow {
  @apply absolute right-6 w-2.5 h-2.5 ml-2.5;
}

.dropdown-button:focus .dropdown-button-inner {
  position: relative;
  left: -1px; /* Border offset compensation */
  top: -1px; /* Border offset compensation */
}

.dropdown-button:focus .dropdown-button-arrow {
  margin-right: -2px; /* Border offset compensation */
}

.dropdown-button.dropdown-warning {
  @apply bg-amber-100 border-amber-500
    focus:ring-amber-500 focus:border-amber-500
}

.dropdown-button.dropdown-danger {
  @apply bg-red-200 border border-red-600
    focus:ring-red-500 focus:border-red-500 focus:border-2
}

.dropdown-button-placeholder {
  @apply truncate pr-4 w-full text-left;
}

.dropdown-menu {
  @apply bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-700;
}

.dropdown-menu-item {
  @apply block px-2.5 py-2
    text-gray-400 dark:text-white dark:hover:text-white
    dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600;
}

.dropdown-warning {
  @apply bg-amber-100 border border-amber-500
    hover:bg-gray-100 hover:text-gray-700
    focus:ring-blue-500 focus:border-blue-500 focus:border-2
    rounded-lg text-sm px-2.5 py-2.5 text-center inline-flex items-center select-none
    dark:focus:ring-blue-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white;
    box-sizing: border-box;
}

.dropdown-danger {
  @apply bg-red-200 border border-red-600
    hover:bg-gray-100 hover:text-gray-700
    focus:ring-blue-500 focus:border-blue-500 focus:border-2
    rounded-lg text-sm px-2.5 py-2.5 text-center inline-flex items-center select-none
    dark:focus:ring-blue-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white;
    box-sizing: border-box;
}

.dropdown-danger,
.dropdown-validation-error {
  @apply bg-red-100 border border-red-600
    hover:bg-red-200 hover:text-gray-700
    focus:ring-blue-500 focus:border-blue-500 focus:border-2
    rounded-lg text-sm px-2.5 py-2.5 text-center inline-flex items-center select-none
    dark:focus:ring-blue-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white;
    box-sizing: border-box;
}

.dropdown-validation-message {
  @apply text-red-600
    text-sm /* px-2.5 */ py-2.5 text-center inline-flex items-center select-none
}

.pl-12 {
  padding-left: 48px;
}

.pl-16 {
  padding-left: 64px;
}

.pl-20 {
  padding-left: 80px;
}

.pl-24 {
  padding-left: 96px;
}
</style>
